import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { useTheme } from '../../../contexts/ThemeContext';
import { useDepartments } from '../../../hooks/useDepartments';
import { stateDepartmentsService } from '../../../services/stateDepartmentsService';
import LoadingSpinner from '../../common/LoadingSpinner';
import Button from '../../ui/Button';
import Select from '../../ui/Select';
import DebouncedInput from '../../ui/DebouncedInput';
import UniversalFilters from '../../common/UniversalFilters';
import { FILTER_CONFIGS, getFilterConfig } from '../../../constants/filterConfigs';
import { useFilters, useFilteredData } from '../../../hooks/useFilters';
import {
  DEFAULT_STATE_DEPARTMENTS_DATA,
  formatDepartmentAmount,
  calculateDepartmentsTotal,
  getAvailableDepartments
} from '../../../constants/stateDepartmentsConstants';
import { validateDepartmentsData, sanitizeDepartmentsData } from '../../../utils/stateDepartmentsValidation';
import { financialYearUtils, debounce } from '../../../utils/helpers';

const StateDepartments = () => {
  const { user } = useAuth();
  const { isMobile, getResponsiveValue } = useTheme();
  const { departments, loading: loadingDepartments, getDepartmentLabel, getDepartmentOptions } = useDepartments();
  
  // Auto-select current financial year
  const currentFinancialYear = financialYearUtils.getCurrentFinancialYear();
  
  const {
    filters,
    handleFilterChange,
    clearFilters,
    hasActiveFilters
  } = useFilters({
    search: '',
    financialYear: currentFinancialYear
  });

  const [departmentsData, setDepartmentsData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [success, setSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [saving, setSaving] = useState({});
  
  const [selectedDepartmentValue, setSelectedDepartmentValue] = useState('');

  const filterConfig = getFilterConfig('STATE_DEPARTMENTS');

  const titleSize = getResponsiveValue({
    mobile: 'text-xl',
    desktop: 'text-2xl'
  });

  const cardPadding = getResponsiveValue({
    mobile: 'p-4',
    desktop: 'p-6'
  });

  // Debounced auto-save function
  const debouncedAutoSave = useCallback(
    debounce(async (financialYear, updatedDepartmentsData) => {
      try {
        if (!financialYear || !updatedDepartmentsData) {
          console.error('Cannot save: Missing required data');
          return;
        }

        setSaving(prev => ({ ...prev, [financialYear]: true }));
        
        const sanitizedData = sanitizeDepartmentsData(updatedDepartmentsData);
        const result = await stateDepartmentsService.saveDepartmentsData(sanitizedData, user.uid);
        
        setSaving(prev => ({ ...prev, [financialYear]: false }));
        
        if (result.success) {
          setSuccess(true);
          setSuccessMessage('Changes saved automatically');
          setTimeout(() => {
            setSuccess(false);
            setSuccessMessage('');
          }, 2000);
          
          setErrors({});
        } else {
          throw new Error(result.message || 'Failed to save departments data');
        }
      } catch (error) {
        console.error('Auto-save error:', error);
        setSaving(prev => ({ ...prev, [financialYear]: false }));
        setErrors({ general: `Failed to save changes: ${error.message}` });
      }
    }, 1500),
    [user.uid]
  );

  useEffect(() => {
    if (filters.financialYear) {
      loadDepartmentsData();
    } else {
      setDepartmentsData(null);
    }
  }, [filters.financialYear]);

  useEffect(() => {
    if (currentFinancialYear) {
      loadDepartmentsData();
    }
  }, []);

  const loadDepartmentsData = async () => {
    try {
      setLoading(true);
      setErrors({});
      
      const result = await stateDepartmentsService.getDepartmentsData(filters.financialYear);
      
      if (result.success && result.data) {
        setDepartmentsData(result.data);
      } else {
        setDepartmentsData({
          ...DEFAULT_STATE_DEPARTMENTS_DATA,
          financialYear: filters.financialYear
        });
      }
    } catch (error) {
      console.error('Error loading departments data:', error);
      setErrors({ general: error.message });
    } finally {
      setLoading(false);
    }
  };

  const getFilteredDepartments = () => {
    if (!departmentsData?.departments) return [];
    
    let filteredDepts = departmentsData.departments;
    
    if (filters.search && filters.search.trim()) {
      const searchLower = filters.search.toLowerCase().trim();
      filteredDepts = filteredDepts.filter(dept => 
        dept.name.toLowerCase().includes(searchLower)
      );
    }
    
    filteredDepts = filteredDepts.sort((a, b) => {
      const deptA = departments.find(d => d.departmentCode === (a.value || a.id));
      const deptB = departments.find(d => d.departmentCode === (b.value || b.id));
      
      const priorityA = deptA?.priority || 999;
      const priorityB = deptB?.priority || 999;
      
      return priorityA - priorityB;
    });
    
    return filteredDepts;
  };

  const getAvailableDepartmentOptions = () => {
    if (!departmentsData || loadingDepartments) return [];
    
    const allDepartmentOptions = getDepartmentOptions();
    const addedDepartmentCodes = departmentsData.departments.map(d => d.value || d.id);
    const availableDepartments = allDepartmentOptions.filter(
      dept => !addedDepartmentCodes.includes(dept.value)
    );
    
    return [
      { value: '', label: 'Select a department to add...' },
      ...availableDepartments
    ];
  };

  const handleAddDepartment = () => {
    if (!selectedDepartmentValue) return;
    
    if (!filters.financialYear) {
      setErrors({ general: 'Please select a financial year first' });
      return;
    }
    
    const selectedDept = departments.find(dept => dept.departmentCode === selectedDepartmentValue);
    if (!selectedDept) return;
    
    const updatedData = {
      ...departmentsData,
      financialYear: filters.financialYear,
      departments: [
        ...departmentsData.departments,
        {
          id: selectedDept.departmentCode,
          value: selectedDept.departmentCode,
          name: selectedDept.departmentName,
          amount: 0
        }
      ]
    };
    
    setDepartmentsData(updatedData);
    setSelectedDepartmentValue('');
    if (success) setSuccess(false);
    
    debouncedAutoSave(filters.financialYear, updatedData);
  };

  // Handle amount change with auto-save
  const handleAmountChange = (index, value) => {
    if (!filters.financialYear) {
      setErrors({ general: 'Please select a financial year first' });
      return;
    }

    const updatedData = {
      ...departmentsData,
      financialYear: filters.financialYear,
      departments: departmentsData.departments.map((dept, i) => 
        i === index ? { ...dept, amount: value } : dept
      )
    };
    
    setDepartmentsData(updatedData);
    setErrors(prev => ({ ...prev, [`departments.${index}.amount`]: '' }));
    if (success) setSuccess(false);
    
    debouncedAutoSave(filters.financialYear, updatedData);
  };

  const handleRemoveDepartment = (index) => {
    if (!filters.financialYear) {
      setErrors({ general: 'Please select a financial year first' });
      return;
    }

    const updatedData = {
      ...departmentsData,
      financialYear: filters.financialYear,
      departments: departmentsData.departments.filter((_, i) => i !== index)
    };
    
    setDepartmentsData(updatedData);
    debouncedAutoSave(filters.financialYear, updatedData);
    
    setSuccess(true);
    setSuccessMessage('Department removed successfully');
    setTimeout(() => {
      setSuccess(false);
      setSuccessMessage('');
    }, 2000);
  };

  const filteredDepartments = getFilteredDepartments();
  const totalAmount = departmentsData ? calculateDepartmentsTotal(filteredDepartments) : 0;
  const availableDepartmentOptions = getAvailableDepartmentOptions();
  const hasAvailableDepartments = availableDepartmentOptions.length > 1;
  const isSaving = saving[filters.financialYear];

  if (loadingDepartments) {
    return (
      <div className="min-h-screen bg-bg-secondary flex items-center justify-center">
        <LoadingSpinner size="xl" text="Loading departments..." />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Success Message */}
      {success && (
        <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-xl p-3 shadow-sm">
          <div className="flex items-center">
            <svg className="w-4 h-4 text-green-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            <span className="text-green-700 font-medium text-sm">{successMessage}</span>
            {isSaving && (
              <div className="ml-2 flex items-center">
                <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-green-500"></div>
                <span className="ml-1 text-xs text-green-600">Saving...</span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className={`${titleSize} font-bold text-text-primary mb-1`}>State Share Allocation (50%)</h1>
          <p className="text-text-secondary text-sm">
            Manage department-wise budget allocations by financial year. Changes are saved automatically.
          </p>
        </div>

        <UniversalFilters
          config={filterConfig}
          values={filters}
          onChange={handleFilterChange}
          onClearFilters={clearFilters}
          mode="dropdown"
        />
      </div>

      {/* Departments Table */}
      {filters.financialYear && (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <LoadingSpinner size="lg" text="Loading departments data..." />
            </div>
          ) : departmentsData ? (
            <div className="space-y-4">
              {/* General Error */}
              {errors.general && (
                <div className="bg-gradient-to-r from-red-50 to-pink-50 border border-red-200 rounded-xl p-3 mx-4 mt-4 shadow-sm">
                  <div className="flex items-center">
                    <svg className="w-4 h-4 text-red-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                    <span className="text-red-700 text-sm">{errors.general}</span>
                  </div>
                </div>
              )}

              {/* Add Department Section */}
              {hasAvailableDepartments && (
                <div className="px-4 pt-4 border-b border-gray-200">
                  <div className="flex items-center space-x-4 pb-4">
                    <div className="flex-1">
                      <Select
                        value={selectedDepartmentValue}
                        onChange={(e) => setSelectedDepartmentValue(e.target.value)}
                        options={availableDepartmentOptions}
                        className="w-full"
                        placeholder="Select a department to add..."
                      />
                    </div>
                    <Button
                      onClick={handleAddDepartment}
                      disabled={!selectedDepartmentValue}
                      size="xl"
                      className="flex items-center space-x-2 px-4 py-2 whitespace-nowrap"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                      </svg>
                      <span>Add Department</span>
                    </Button>
                  </div>
                </div>
              )}

              {/* Departments Table */}
              {filteredDepartments.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="min-w-full text-xs">
                    <thead className="bg-gradient-to-r from-gray-50 to-gray-100">
                      <tr>
                        <th className="px-3 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider border-r border-gray-200 w-16">
                          S.No
                        </th>
                        <th className="px-3 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider border-r border-gray-200 min-w-40">
                          Department Name
                        </th>
                        <th className="px-3 py-3 text-center text-xs font-bold text-gray-700 uppercase tracking-wider border-r border-gray-200 w-64">
                          Amount 
                          <br />
                          <span className="text-gray-500 normal-case font-normal">(in lakh)</span>
                        </th>
                        <th className="px-3 py-3 text-center text-xs font-bold text-gray-700 uppercase tracking-wider w-24">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-100">
                      {filteredDepartments.map((dept, index) => (
                        <tr key={dept.id || dept.value || index} className="hover:bg-blue-50 transition-colors duration-200">
                          <td className="px-3 py-3 text-xs text-gray-600 font-semibold border-r border-gray-200">
                            <div className="flex items-center justify-center w-6 h-6 bg-gray-100 rounded-full text-xs font-bold">
                              {index + 1}
                            </div>
                          </td>
                          <td className="px-3 py-3 text-xs text-gray-900 font-semibold border-r border-gray-200">
                            {dept.name}
                          </td>
                          <td className="px-3 py-3 border-r border-gray-200">
                            <div className="flex items-center justify-center space-x-2">
                              <span className="text-xs text-gray-500 font-medium">₹</span>
                              <DebouncedInput
                                type="number"
                                value={dept.amount || ''}
                                onChange={(value) => handleAmountChange(index, value)}
                                delay={1200}
                                className={`
                                  w-32 border border-gray-300 rounded-lg bg-white text-xs px-2 py-1 text-center font-medium
                                  focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500
                                  transition-all duration-200 hover:border-blue-400
                                  ${errors[`departments.${index}.amount`] ? 'border-red-500' : ''}
                                `}
                                placeholder="0.00"
                              />
                            </div>
                            {errors[`departments.${index}.amount`] && (
                              <div className="text-xs text-red-600 mt-1 text-center">
                                {errors[`departments.${index}.amount`]}
                              </div>
                            )}
                          </td>
                          <td className="px-3 py-3 text-center">
                            <button
                              onClick={() => handleRemoveDepartment(index)}
                              className="p-1 rounded-lg hover:bg-red-100 transition-colors text-red-600 hover:text-red-700"
                              title="Remove department"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                              </svg>
                            </button>
                          </td>
                        </tr>
                      ))}

                      {/* Total Row */}
                      <tr className="bg-gray-700 text-white font-bold border-t-4 border-gray-600">
                        <td className="px-3 py-3 border-r border-gray-600"></td>
                        <td className="px-3 py-3 text-xs font-bold border-r border-gray-600">
                          Total {filters.search ? '(Filtered)' : ''}
                        </td>
                        <td className="px-3 py-3 text-center text-sm font-bold border-r border-gray-600">
                          {formatDepartmentAmount(totalAmount)}
                        </td>
                        <td className="px-3 py-3"></td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-12 px-4">
                  <div className="text-gray-600">
                    {filters.search ? 
                      `No departments match "${filters.search}". Try adjusting your search.` :
                      hasAvailableDepartments ? 
                        "No departments added yet. Use the dropdown above to add departments." :
                        "All available departments have been added."
                    }
                  </div>
                </div>
              )}

              {/* Auto-save Status */}
              <div className="flex justify-between items-center px-4 py-3 bg-gray-50">
                <div className="text-sm text-gray-600">
                  Changes are saved automatically as you type
                </div>
                {isSaving && (
                  <div className="flex items-center text-sm text-blue-600">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500 mr-2"></div>
                    Saving changes...
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="text-center py-12 px-4">
              <div className="text-gray-600">
                Failed to load departments data. Please try again.
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default StateDepartments;